import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { Spinner } from '@/components/ui/Spinner';
import { datumDe, datumZeitDe, relativZeitDe } from '@/lib/format';
import type { DetailAuditEintrag } from '@/types/detail';
import { zustandAnzeige } from '@/lib/zustandLabel';
import { NutzerChip } from '@/components/ui/NutzerChip';

const NACHLADE_GROESSE = 20;

// Technische Feldnamen in Klartext (B34). Was hier nicht steht, faellt auf
// den Rohnamen zurueck — lieber ein technischer Begriff als gar nichts.
const FELD_LABEL: Record<string, string> = {
  zustand_id: 'Zustand',
  notiz: 'Notiz',
  // "Termin" statt "Abholdatum" — derselbe Wortlaut wie im Kopf der
  // Bestellung und in der Dashboard-Spalte (B111).
  abholdatum_zugesagt: 'Termin',
  wiedervorlage: 'Wiedervorlage',
  lieferadresse: 'Lieferadresse geändert',
  // A2: Der Eintrag trägt alten und neuen Anzeigenamen in den Labels —
  // deshalb wird er unten eigens behandelt und nicht hier abgefertigt.
  kunde: 'Kundenangaben geändert',
  versandart_id: 'Versandart',
  abgeholt_am: 'Sendung abgeholt',
  versendet: 'Sendung versendet',
  // A3.2: Der orders/updated-Webhook vermerkt nur, dass die Anschrift im
  // Shop abweicht — übernommen wird sie nicht. Deshalb „geändert in
  // Shopify" und nicht „Lieferadresse geändert": Letzteres hieße bei uns.
  shopify_adresse_geaendert: 'Lieferanschrift in Shopify geändert',
  // Der Storno-Webhook schreibt diesen Eintrag seit es ihn gibt; die
  // Beschriftung fehlte, im Verlauf stand der rohe Feldname. Ob der
  // Zustand mitgewechselt hat, sagt der Eintrag bewusst nicht — dann
  // steht der Zustandswechsel ohnehin als eigene Zeile daneben.
  shopify_storniert: 'In Shopify storniert',
  // A30: Der Geschenkwunsch lässt sich nachtragen — und was nachgetragen
  // wird, steht im Verlauf. Der Eintrag nennt beide Seiten (unten eigens
  // behandelt), damit man sieht, ob jemand ihn gesetzt oder wieder
  // zurückgenommen hat.
  ist_geschenk: 'Geschenkwunsch',
  // A52 (#133): Die Zuordnung steht im Verlauf BEIDER Bestellungen,
  // deshalb zwei Felder mit verschiedener Richtung — „ich gehöre zu"
  // und „mir gehört". Beide werden unten eigens behandelt, damit die
  // Nummer der Gegenseite mit dasteht.
  zugeordnet_zu: 'Zuordnung',
  zuordnung_erhalten: 'Zuordnung',
  // A56 (#135): der Kundenwunsch. Alle drei werden unten eigens
  // behandelt, damit die Richtung mit dasteht — „von komplett auf
  // sofort" sagt mehr als „Lieferwunsch".
  lieferwunsch: 'Lieferwunsch',
  nachricht_wunsch: 'Benachrichtigung',
  position_nachricht_wunsch: 'Benachrichtigung'
};

const CHECKLIST_LABEL: Record<string, string> = {
  bestellt: 'Bestellt',
  eingetroffen: 'Eingetroffen',
  kunde_benachrichtigt: 'Kunde benachrichtigt',
  rechnung_geschrieben: 'Rechnung geschrieben',
  bezahlt: 'Bezahlt',
  abgeholt: 'Abgeholt'
};

const POSITION_AKTION: Record<string, string> = {
  add: 'Position hinzugefügt',
  delete: 'Position entfernt',
  menge: 'Position · Menge geändert',
  menge_eingegangen: 'Position · Eingang gebucht',
  notiz: 'Position · Notiz geändert',
  titel: 'Position · Titel geändert'
};

/**
 * Was der Verlauf über die Bestellung wissen muss, um Zustandsnamen
 * richtig zu schreiben (A11.2).
 *
 * Ohne diesen Zusammenhang bliebe es beim Label aus dem Eintrag — und der
 * Verlauf sagte „Abgeschlossen", während Kopf und Brett derselben
 * Bestellung „Versendet" sagen.
 */
export type ZustandKontext = {
  versandartCode: string | null;
  /** Die gepflegte Zustandsliste, für die Auflösung ID → Code. */
  zustaende: { id: number; code: string }[];
};

/** Zustandsname eines Verlaufseintrags, aufgelöst über seine ID. */
function zustandName(id: unknown, label: string, ctx?: ZustandKontext): string {
  if (!ctx || typeof id !== 'number') return label;
  const code = ctx.zustaende.find((z) => z.id === id)?.code;
  // Ohne Treffer bleibt das Label stehen: ein Zustand, den die Liste
  // nicht mehr kennt, ist mit seinem alten Namen immer noch besser
  // benannt als gar nicht.
  return code === undefined ? label : zustandAnzeige(code, label, ctx.versandartCode);
}

/**
 * „2026-08-30 · #1Z999" → „30.08.2026 · #1Z999" (A49).
 *
 * Das Protokoll speichert das Datum so, wie es in der Spalte steht
 * (ISO). Gelesen wird es deutsch — wie jedes andere Datum im Verlauf
 * auch (B72). Übersetzt wird nur der vordere Teil; die Sendungsnummer
 * bleibt Zeichen für Zeichen stehen, sie ist keine Zahl und kein Datum.
 */
export function sendungsangabeDe(label: string): string {
  const [datum, ...rest] = label.split(' · ');
  const vorne = /^\d{4}-\d{2}-\d{2}$/.test(datum) ? datumDe(datum) : datum;
  return [vorne, ...rest].join(' · ');
}

export function eintragText(e: DetailAuditEintrag, ctx?: ZustandKontext): string {
  const feld = e.feld;

  // Sub-Entitaeten ohne eigenes Feld: aus Typ und Aktion beschreiben.
  if (feld === null || feld === undefined) {
    if (e.entity_type === 'sendung') {
      return e.action === 'create' ? 'Sendung angelegt' : 'Sendung geändert';
    }
    if (e.entity_type === 'kommunikation') {
      return e.action === 'create' ? 'Kommunikation erfasst' : 'Kommunikation bearbeitet';
    }
    return e.action === 'create' ? 'Bestellung erstellt' : e.action;
  }

  if (feld === 'zustand_id') {
    // Ob die Maschine oder ein Mensch entschieden hat, gehört sichtbar in
    // den Verlauf (A7.3). Wer den Wareneingang bucht, löst den
    // Zustandswechsel aus — entschieden hat er ihn nicht, und im Verlauf
    // stünde sonst sein Name neben einer Entscheidung, die er nie
    // getroffen hat.
    const wie = e.automatisch === true ? ' (automatisch)' : '';
    if (!e.von_label || !e.auf_label) return `Zustand geändert${wie}`;
    // A11.2: „Bereit" und „Abgeschlossen" heissen je nach Weg anders.
    // Aufgelöst wird über die Zustands-ID, die der Eintrag mitträgt —
    // nicht über das Label: ein Label kann der Admin umbenennen, eine ID
    // nicht.
    const von = zustandName(e.von, e.von_label, ctx);
    const auf = zustandName(e.auf, e.auf_label, ctx);
    return `Zustand: ${von} → ${auf}${wie}`;
  }
  // Datumswerte deutsch, nicht im ISO-Format aus der Datenbank (B72).
  if (feld === 'abholdatum_zugesagt') return `Termin: ${datumDe(e.auf as string | null)}`;
  if (feld === 'wiedervorlage') return `Wiedervorlage: ${datumDe(e.auf as string | null)}`;
  if (feld === 'versandart_id') return 'Versandart gesetzt';

  // A56: der Kundenwunsch, mit Richtung. Die Labels kommen aus dem
  // Protokoll — sie stehen dort, wie sie beim SCHREIBEN galten, und
  // werden hier nicht neu gebildet. Wer eine Beschriftung später
  // ändert, schreibt damit die Vergangenheit nicht um.
  if (feld === 'lieferwunsch') {
    return e.von_label && e.auf_label
      ? `Liefern: ${e.von_label} → ${e.auf_label}`
      : 'Lieferwunsch geändert';
  }
  if (feld === 'nachricht_wunsch') {
    return e.auf === true ? 'Nachricht gewünscht, sobald alles da ist' : 'Kein Nachrichtenwunsch mehr';
  }
  if (feld === 'position_nachricht_wunsch') {
    return e.auf_label ?? 'Benachrichtigungswunsch geändert';
  }

  // A52: Zuordnung und Rücknahme, mit der Nummer der Gegenseite.
  // Ohne sie stünde im Verlauf nur „Zuordnung" — und wer nachsieht,
  // fragt sich, zu welcher.
  if (feld === 'zugeordnet_zu' || feld === 'zuordnung_erhalten') {
    const nr = (e.auf_label ?? e.von_label ?? '') as string;
    const gesetzt = e.auf !== null && e.auf !== undefined;
    if (feld === 'zugeordnet_zu') {
      return gesetzt
        ? `Zugeordnet zu ${nr}`
        : `Zuordnung zu ${nr} aufgehoben`;
    }
    return gesetzt
      ? `${nr} zugeordnet`
      : `Zuordnung von ${nr} aufgehoben`;
  }

  // A49: Buchung und Korrektur einer Sendung, mit Richtung.
  //
  // Bis hierher stand hier nur „Sendung versendet" bzw. „Sendung
  // abgeholt" — ohne Datum, ohne Nummer. Der Eintrag trug die Werte
  // zwar, aber unter eigenen Schlüsseln, und die reicht der
  // Verlaufs-Leser (api/lib/audit.php) gar nicht durch. Wer eine
  // Sendungsnummer nachträgt, soll im Verlauf sehen, was vorher
  // dastand.
  //
  // ÄLTERE EINTRÄGE HABEN KEINE RICHTUNG. Sie tragen weder von_label
  // noch auf_label; dann bleibt es beim alten Wortlaut. Vergangenes
  // wird nicht umgeschrieben.
  if (feld === 'versendet' || feld === 'abgeholt_am') {
    const was = feld === 'versendet' ? 'Versand' : 'Abholung';
    if (!e.auf_label) return FELD_LABEL[feld] ?? feld;
    // Ohne vorherigen Wert ist es die erste Buchung, keine Korrektur —
    // und ein „— →" davor wäre ein Pfeil, der aus dem Nichts kommt.
    const vorher = e.von_label;
    return vorher === null || vorher === '' || vorher === '—'
      ? `${was} gebucht: ${sendungsangabeDe(e.auf_label)}`
      : `${was}: ${sendungsangabeDe(vorher)} → ${sendungsangabeDe(e.auf_label)}`;
  }
  // A30: Die Richtung zählt. „Geschenkwunsch geändert" ließe offen, was
  // jetzt gilt — und genau das will man im Verlauf ablesen können.
  if (feld === 'ist_geschenk') {
    return e.auf ? 'Als Geschenk gewünscht' : 'Geschenkwunsch zurückgenommen';
  }
  // A2: Der Name steht im Eintrag, nicht nur die Tatsache einer
  // Änderung — sonst müsste man raten, was korrigiert wurde.
  if (feld === 'kunde') {
    return e.von_label && e.auf_label
      ? `Kunde: ${e.von_label} → ${e.auf_label}`
      : 'Kundenangaben geändert';
  }

  if (feld.startsWith('checklist:')) {
    const code = feld.slice('checklist:'.length);
    const label = CHECKLIST_LABEL[code] ?? code;
    return `Checkliste · ${label}: ${e.auf ? 'erledigt' : 'offen'}`;
  }

  // Format: position:<id>:<aktion>
  if (feld.startsWith('position:')) {
    const aktion = feld.split(':')[2] ?? '';
    // Beschaffung: anlegen und löschen tragen denselben Feldnamen und
    // unterscheiden sich nur daran, auf welcher Seite das Label steht —
    // ein eigener Feldname je Richtung wäre eine zweite Konvention für
    // dieselbe Sache.
    if (aktion === 'beschaffung') {
      return e.auf_label
        ? `Beschaffung erfasst: ${e.auf_label}`
        : `Beschaffung gelöscht: ${e.von_label ?? '—'}`;
    }
    // Die Meldung des Lieferanten am bestehenden Eintrag (E11). Der
    // Eintrag selbst bleibt dabei stehen — deshalb ein eigener Feldname
    // und kein „Beschaffung geändert".
    if (aktion === 'beschaffung_meldung') {
      const weg = e.von_label ?? '—';
      return e.auf === null || e.auf === undefined || e.auf === ''
        ? `Meldung entfernt: ${weg}`
        : `Meldung ${weg}: ${String(e.auf)}`;
    }
    // A1: Wie beim Kunden zählt der Wortlaut, nicht die Tatsache.
    if (aktion === 'titel' && typeof e.von === 'string' && typeof e.auf === 'string') {
      return `Position · Titel: ${e.von} → ${e.auf}`;
    }
    return POSITION_AKTION[aktion] ?? `Position · ${aktion}`;
  }

  return FELD_LABEL[feld] ?? feld;
}

type Props = {
  bestellungId: number;
  eintraege: DetailAuditEintrag[];
  /**
   * Versandart und Zustandsliste — daraus entstehen die Zustandsnamen
   * (A11.2). Kommt als Prop und nicht aus einem Hook: die Sektion soll
   * ohne Query-Provider prüfbar bleiben, und die Route hält die Liste
   * ohnehin schon.
   */
  zustandKontext?: ZustandKontext;
  /** Ob es ueber die mitgelieferten Eintraege hinaus noch weitere gibt. */
  hatMehr?: boolean;
};

export function AuditLogSektion({
  bestellungId,
  eintraege,
  zustandKontext,
  hatMehr = true
}: Props) {
  const [open, setOpen] = useState(false);
  const [nachgeladen, setNachgeladen] = useState<DetailAuditEintrag[]>([]);
  const [mehrDa, setMehrDa] = useState(hatMehr);
  const [laedt, setLaedt] = useState(false);
  const toast = useToast();

  if (eintraege.length === 0) return null;

  const alle = [...eintraege, ...nachgeladen];
  const letzter = eintraege[0];

  async function mehrLaden(): Promise<void> {
    if (laedt) return;
    setLaedt(true);
    try {
      const res = await apiFetch<{ eintraege: DetailAuditEintrag[]; hat_mehr: boolean }>(
        `/bestellungen/verlauf.php?bestellung_id=${bestellungId}` +
          `&offset=${alle.length}&limit=${NACHLADE_GROESSE}`
      );
      setNachgeladen((v) => [...v, ...res.eintraege]);
      setMehrDa(res.hat_mehr);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      toast('error', `Verlauf konnte nicht geladen werden: ${code}`);
    } finally {
      setLaedt(false);
    }
  }

  return (
    <section className="text-xs text-ink-soft">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="inline-flex min-h-[44px] items-center gap-1.5 text-left hover:text-akzent"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Letzte Änderung:{' '}
        {/* S2-006b.2: Datum und Uhrzeit sichtbar, wie im
            Kommunikationsprotokoll darüber. Vorher standen auf derselben
            Seite zwei Protokolle mit zwei verschiedenen Zeitangaben —
            und die genaue Zeit lag im title, also im Hover: auf dem
            Tablet unerreichbar. Die relative Angabe rückt ins title
            nach. */}
        <span className="tabular-nums" title={`${relativZeitDe(letzter.erstellt_am)} geändert`}>
          {datumZeitDe(letzter.erstellt_am)}
        </span>
        {letzter.user && (
          <>
            {' · '}
            {/* Derselbe Farbpunkt wie am Kommunikations-Eintrag und am
                Erfasser-Merkzeichen. Der Verlauf ist die einzige Ansicht,
                die ausschliesslich davon handelt, wer etwas getan hat —
                hier trug das Kuerzel als einziges keine Farbe. */}
            {/* A9.1: Der Chip statt des Farbpunkts. Wer eine Zeile
                überfliegt, erkennt am Zeichen, ob sie von ihm stammt —
                das Kürzel daneben bleibt für den zweiten Blick. */}
            <span className="inline-flex items-center gap-1">
              <NutzerChip
                farbe={letzter.user.farbe}
                symbol={letzter.user.symbol}
                kuerzel={letzter.user.kuerzel}
                name={letzter.user.name}
                groesse="klein"
              />
              {/* Nur wenn der Chip ein SYMBOL traegt (A33, nachgezogen in
                  A36.4): Ohne Symbol stehen die Initialen IM Chip, und
                  daneben noch einmal dieselben zwei Buchstaben ergibt
                  „PP PP". */}
              {letzter.user.symbol !== null && letzter.user.kuerzel}
            </span>
          </>
        )}{' '}
        · {eintragText(letzter)}
      </button>
      {open && (
        <>
          <ul className="mt-2 space-y-1 border-l border-line pl-3">
            {alle.map((e) => (
              // Zusammengesetzter Key: die id ist nur je Quelle eindeutig,
              // seit der Verlauf audit_log und kommunikation_log mischt.
              <li key={`${e.entity_type}-${e.id}-${e.action}`} className="flex gap-2">
                {/* S2-006b.2: Absolut sichtbar, relativ im title.
                    Bis B65 war es umgekehrt begründet — „relativ zum
                    Überfliegen". Am Tresen zählt aber, WANN etwas
                    geschah, nicht wie lange es her ist; und was nur im
                    Hover steht, gibt es auf dem Tablet nicht. */}
                <span
                  className="shrink-0 tabular-nums text-ink-faint"
                  title={`${relativZeitDe(e.erstellt_am)} geändert`}
                >
                  {datumZeitDe(e.erstellt_am)}
                </span>
                {e.user && (
                  <span className="inline-flex shrink-0 items-center gap-1 font-medium">
                    <NutzerChip
                      farbe={e.user.farbe}
                      symbol={e.user.symbol}
                      kuerzel={e.user.kuerzel}
                      name={e.user.name}
                      groesse="klein"
                    />
                    {e.user.symbol !== null && e.user.kuerzel}
                  </span>
                )}
                <span>{eintragText(e, zustandKontext)}</span>
              </li>
            ))}
          </ul>
          {mehrDa && (
            <button
              type="button"
              onClick={() => void mehrLaden()}
              disabled={laedt}
              className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 pl-3 text-ink-soft hover:text-akzent disabled:text-ink-faint"
            >
              {laedt && <Spinner />}
              {laedt ? 'Lädt …' : `Mehr anzeigen (${NACHLADE_GROESSE})`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
